/**
 * Notification Center API client.
 *
 * Kept in the feature folder rather than bolted onto the 1,300-line
 * services/api.ts: the Notification Center is a self-contained surface and its
 * twenty-odd endpoints do not need to be in everyone's import graph.
 */

import api from '../../services/api'
import type {
  AudienceDefinition, AudiencePreview, Campaign, CampaignPerformance,
  Delivery, EngineSettings, Funnel, Overview, Segment, Template,
  WhatsAppAnalytics,
} from './types'

const BASE = '/admin/notifications'

/** The backend answers { success, data }. Everything here returns `data`. */
const unwrap = <T,>(response: { data: { data: T } }): T => response.data.data

export const notificationsApi = {
  // ── Campaigns ─────────────────────────────────────────────────────────────
  listCampaigns: async (params?: { status?: string; category?: string; q?: string; limit?: number; offset?: number }) =>
    unwrap<{ campaigns: Campaign[]; total: number }>(await api.get(`${BASE}/campaigns`, { params })),

  getCampaign: async (id: string) =>
    unwrap<{
      campaign: Campaign
      analytics: Funnel & {
        suppression: { reason: string; count: number }[]
        events: Record<string, number>
        failures: { error: string; count: number }[]
      }
    }>(
      await api.get(`${BASE}/campaigns/${id}`)
    ),

  createCampaign: async (payload: Partial<Campaign>) =>
    unwrap<Campaign>(await api.post(`${BASE}/campaigns`, payload)),

  updateCampaign: async (id: string, payload: Partial<Campaign>) =>
    unwrap<Campaign>(await api.patch(`${BASE}/campaigns/${id}`, payload)),

  deleteCampaign: async (id: string) =>
    unwrap<{ deleted: boolean }>(await api.delete(`${BASE}/campaigns/${id}`)),

  duplicateCampaign: async (id: string) =>
    unwrap<Campaign>(await api.post(`${BASE}/campaigns/${id}/duplicate`)),

  submitCampaign: async (id: string) =>
    unwrap<Campaign>(await api.post(`${BASE}/campaigns/${id}/submit`)),

  approveCampaign: async (id: string) =>
    unwrap<Campaign>(await api.post(`${BASE}/campaigns/${id}/approve`)),

  rejectCampaign: async (id: string, reason?: string) =>
    unwrap<Campaign>(await api.post(`${BASE}/campaigns/${id}/reject`, { reason })),

  /** Omit sendAt to start immediately; the worker picks it up within a minute. */
  scheduleCampaign: async (id: string, sendAt?: string | null) =>
    unwrap<{ campaign: Campaign; message: string }>(
      await api.post(`${BASE}/campaigns/${id}/schedule`, { send_at: sendAt ?? null })
    ),

  pauseCampaign: async (id: string) =>
    unwrap<{ campaign: Campaign; stopped: number }>(await api.post(`${BASE}/campaigns/${id}/pause`)),

  resumeCampaign: async (id: string) =>
    unwrap<{ campaign: Campaign; requeued: number }>(await api.post(`${BASE}/campaigns/${id}/resume`)),

  /** Goes to the signed-in admin unless explicit ids are given. Never counted in campaign stats. */
  testSendCampaign: async (id: string, userIds?: string[]) =>
    unwrap<{ targets: string[]; sent: number; suppressed: number; failed: number }>(
      await api.post(`${BASE}/campaigns/${id}/test`, { user_ids: userIds })
    ),

  listDeliveries: async (id: string, params?: { status?: string; limit?: number; offset?: number }) =>
    unwrap<{ deliveries: Delivery[]; total: number }>(await api.get(`${BASE}/campaigns/${id}/deliveries`, { params })),

  // ── Audience ──────────────────────────────────────────────────────────────
  previewAudience: async (definition: AudienceDefinition) =>
    unwrap<AudiencePreview>(await api.post(`${BASE}/audience/preview`, definition)),

  listSegments: async () => unwrap<Segment[]>(await api.get(`${BASE}/segments`)),

  // ── Content library ───────────────────────────────────────────────────────
  listTemplates: async (params?: { category?: string; tag?: string; q?: string }) =>
    unwrap<Template[]>(await api.get(`${BASE}/templates`, { params })),

  createTemplate: async (payload: Partial<Template>) =>
    unwrap<Template>(await api.post(`${BASE}/templates`, payload)),

  updateTemplate: async (id: string, payload: Partial<Template>) =>
    unwrap<Template>(await api.patch(`${BASE}/templates/${id}`, payload)),

  archiveTemplate: async (id: string) =>
    unwrap<{ archived: boolean }>(await api.delete(`${BASE}/templates/${id}`)),

  // ── Analytics + settings ──────────────────────────────────────────────────
  getOverview: async (range = 'today') =>
    unwrap<Overview>(await api.get(`${BASE}/analytics/overview`, { params: { range } })),

  getCampaignPerformance: async (range = '30d') =>
    unwrap<CampaignPerformance[]>(await api.get(`${BASE}/analytics/campaigns`, { params: { range } })),

  /** WhatsApp marketing delivery, classified - never a single "failed" bucket. */
  getWhatsAppAnalytics: async (campaign?: string) =>
    unwrap<WhatsAppAnalytics>(await api.get(`${BASE}/analytics/whatsapp`, { params: campaign ? { campaign } : {} })),

  getSettings: async () => unwrap<EngineSettings>(await api.get(`${BASE}/settings`)),

  updateSettings: async (payload: Record<string, string | number | boolean>) =>
    unwrap<{ updated: number }>(await api.patch(`${BASE}/settings`, payload)),
}
